<script lang="ts">
  import { navigate } from '../lib/router.svelte';
  import AdminShell from './AdminShell.svelte';
  import PageContainer from '../lib/ui/PageContainer.svelte';
  import StateBlock from '@ui/StateBlock.svelte';
  import TopContent from '../lib/ui/TopContent.svelte';
  import ContentSummary from '../lib/ui/ContentSummary.svelte';
  import ContentTiming from '../lib/ui/ContentTiming.svelte';
  import CategoryTable from '../lib/ui/CategoryTable.svelte';
  import PageHeader from '../lib/ui/PageHeader.svelte';
  import {
    redirectIfAwaitingOnboarding,
    getToken,
    handle401,
    type AdminUser,
    type TopContent as TopContentData,
  } from '../lib/admin';
  import { withCommunity, onCommunityChange } from '../lib/community.svelte';
  import { asAdminUser, ensureBootstrap } from '../lib/session';
  import { ENGAGEMENT_BLOCKS, fetchInsights, fetchTopContent, type InsightsWith } from '@api';

  // `labels` + `timing` are what `/admin/analytics/content` returned; `clock` is the
  // third thing that response carried, and in v1 it is its own block on the one
  // insights route (doc 04 §3.1 H) rather than a field duplicated across two
  // endpoints the Overview then fetched both of.
  type Data = InsightsWith<(typeof ENGAGEMENT_BLOCKS)[number]>;

  let me = $state<AdminUser | null>(null);
  let data = $state<Data | null>(null);
  let loading = $state(true);
  let dataLoading = $state(false);
  let error = $state<string | null>(null);

  // "Top content" group: its own period control + fetch, independent of the page's
  // other widgets. Kept populated across reloads so switching period doesn't flash.
  //
  // STILL ON THE LEGACY ROUTE, deliberately and reported: doc 04 §3.1 H's block
  // list has no key for top COMMENTS, and this widget renders
  // `top_comments_by_upvotes` / `top_comments_by_replies` — two of its four lists.
  // `domain.content.top_comments` exists and is unreachable from `/v1`, so there is
  // nothing to call yet — see `api/v1/insights.py`'s module docstring, which reports
  // the same gap from the server side. `GET …/comments` is not a substitute: it is
  // newest-first with no `sort`, no staff exclusion and no reply count.
  //
  // The POST half is ready and verified (2026-07-29, skools 50 and 19): the
  // `top_posts` block is the same query, and its `by_upvotes`/`by_comments` ids match
  // this route's exactly at all-time. TWO THINGS TO GET RIGHT WHEN IT DOES MOVE:
  //
  //   1. WINDOW OFF BY ONE. `lastFor(30)` sends `30d`, and v1 resolves that to
  //      midnight of (now − 30d) — an inclusive span of THIRTY-ONE days, where the
  //      legacy `days=30` floors to midnight of (now − 29d). Verified: legacy
  //      `days=30` is byte-equal to v1 `last=29d` on both communities, and at `30d`
  //      skool 50 gains two boundary-day posts including a new top row (12 upvotes vs
  //      the current 8). Sending `lastFor(topDays)` here WOULD move the numbers.
  //   2. The widget's period is its own, so `top_posts` cannot ride the page's
  //      `ENGAGEMENT_BLOCKS` request (365d) — it needs a second `fetchInsights` at
  //      `last=<topDays>`, which is still the same two requests this page makes today.
  let topDays = $state(30);
  let topData = $state<TopContentData | null>(null);
  const topKeyOf = (id: number, d: number): string => `${id}:${d}`;

  // Its effect used to be gated on `community.loaded`, which made this the page's
  // ONLY remaining second round trip once the insights read moved to RTT 1. It goes
  // out with the others now, against the id in localStorage.
  // Keyed on the community AND the widget's own period, so changing either refetches.
  // `topDays` is read unconditionally inside `key` — that is the contract.
  const topWatch = onCommunityChange(
    (id) => {
      void fetchTopContent(id, topDays)
        .then((t) => (topData = t))
        .catch((err: unknown) => handle401(err, navigate)); // non-fatal: keep what's shown
    },
    {
      gate: () => !loading,
      key: (id) => {
        const d = topDays;
        return id == null ? null : topKeyOf(id, d);
      },
    },
  );

  async function init(): Promise<void> {
    if (!getToken()) {
      navigate('/admin/login');
      return;
    }
    loading = true;
    error = null;
    try {
      // Page data at RTT 1, in parallel with `/v1/me`, against the id already in
      // localStorage (doc 04 §4.1). No `/admin/me` → `/admin/communities` preamble.
      //
      // The top-content read joins the same round trip even though it is still on
      // the legacy route: `withCommunity` gives it the same stale-id recovery, and
      // it is deliberately NOT awaited — it is a secondary widget and a failure
      // there must not blank the page.
      const td = topDays;
      void withCommunity((skoolId) => {
        topWatch.seen(topKeyOf(skoolId, td));
        return fetchTopContent(skoolId, td);
      })
        .then((t) => (topData = t))
        .catch((err: unknown) => handle401(err, navigate));
      const [boot, insights] = await Promise.all([
        ensureBootstrap(),
        withCommunity((skoolId) => {
          watch.seen(skoolId);
          return fetchInsights(skoolId, ENGAGEMENT_BLOCKS);
        }),
      ]);
      me = asAdminUser(boot);
      if (redirectIfAwaitingOnboarding(me, navigate)) return;
      data = insights;
    } catch (err: unknown) {
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load.';
    } finally {
      loading = false;
    }
  }

  // Reload whenever the shared community selection changes.
  const watch = onCommunityChange((id) => void load(id), { gate: () => !loading });

  async function load(id: number): Promise<void> {
    dataLoading = true;
    error = null;
    try {
      data = await fetchInsights(id, ENGAGEMENT_BLOCKS);
    } catch (err: unknown) {
      // NO `watch.rearm()` here, deliberately — and this is the one page where that
      // is right. The watcher KEEPS the failed key, because rearming would re-enter
      // `load()` the instant `error` is assigned: a tight retry loop against an
      // endpoint that is already failing. The retry is the error block's button,
      // which re-runs `init`. (Moderation rearms because its failure is not
      // self-retriggering.)
      if (handle401(err, navigate)) return;
      error = err instanceof Error ? err.message : 'Failed to load engagement insights.';
      data = null;
    } finally {
      dataLoading = false;
    }
  }

  void init();
</script>

<AdminShell {me}>
  {#if loading}
    <StateBlock state="loading" />
  {:else if error && !data}
    <StateBlock state="error" message={error} onretry={init} />
  {:else}
    <PageContainer>
      <!-- The members-only rule was a full-width notice box under the title. It is a
           filter that governs every number on the page, which is what `scope` is, and
           a page-wide caveat reads better on the header than as a card competing with
           the cards it qualifies. -->
      <PageHeader
        section="Insights"
        title="Engagement"
        updated={data?.last_updated}
        scope="Members only · excludes moderator and owner posts, comments and replies"
      />

      {#if dataLoading}
        <StateBlock state="loading" />
      {:else if data}
        <!-- Content: summary (row 1) · timing (row 2) · category table (row 3) -->
        <!-- No top margin on the first block: the header owns the gap below itself. -->
        <div>
          <ContentSummary labels={data.labels} timing={data.timing} />
        </div>
        <div class="mt-6">
          <ContentTiming timing={data.timing} clock={data.clock} />
        </div>
        <section class="mt-6 rounded-box border hair bg-base-100 p-5">
          <div class="eyebrow mb-1">Engagement by category</div>
          <p class="mb-3 text-xs text-base-content/65">
            Members-only, by post category. Click a category to see its posts, or a column header to
            sort. Index = engagement per post vs the community average; Flood = whether posting more in
            that category lifts engagement (Hungry) or dilutes it (Overwhelmed). Unnamed categories:
            open in Skool to identify, then the pencil to name.
          </p>
          <CategoryTable labels={data.labels.items} />
        </section>

        <!-- Top content: posts + comments, with their own period look-back -->
        {#if topData}
          <TopContent data={topData} bind:days={topDays} class="mt-6" />
        {/if}
      {/if}
    </PageContainer>
  {/if}
</AdminShell>
